// src/pages/try-editor/try-store.ts
import type { DiagramStore } from '@/diagram-persistence/diagram-store';
import { IndexedDbDiagramStore, isIndexedDbAvailable } from '@/diagram-persistence/indexeddb-diagram-store';

let instance: (IndexedDbDiagramStore & DiagramStore) | null | undefined;

/**
 * Lazy singleton store for the anonymous /try editors, backed by its OWN
 * IndexedDB database (`azabu-try-diagrams`) — entirely separate from the
 * signed-in account database (`azabu-diagrams`, see `store-instance.ts`).
 * This is what stops a try session from ever reading or writing a
 * signed-out user's cached diagrams on a shared machine.
 *
 * This store is never synced and never registers an auth identity: unlike
 * `getDiagramStore()`, it does not consult `getAuthIdentity()` or wrap
 * itself in a `SyncedDiagramStore` — it is local-only by construction, for
 * the lifetime of the process. Null when IndexedDB is unavailable (private
 * browsing, storage-blocking extensions); callers degrade the same way
 * `getDiagramStore()`'s callers do.
 *
 * CRITICAL: this function's identity must stay stable — callers (notably
 * `useTrySingletonDiagram` and `useDiagramSessionCore`) list `storeFactory`
 * in effect dependency arrays, so pass this reference directly rather than
 * wrapping it in an inline arrow at call sites.
 */
export function getTryDiagramStore(): DiagramStore | null {
    if (instance === undefined) {
        instance = isIndexedDbAvailable() ? new IndexedDbDiagramStore('azabu-try-diagrams') : null;
    }
    return instance;
}

/** Test-only: clears the module-level singleton so bun test's shared
 *  process doesn't leak the try store across test files. */
export function resetTryDiagramStoreForTests(): void {
    instance = undefined;
}
